import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { Card, Table, Descriptions, Divider, Spin } from "antd";
import { Button } from "reactstrap";
import { Icon } from "../../../components/Component";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
} from "../../../components/Component";
import reportApis from "../../../api/reports";
import dayjs from "dayjs";

function MaterialBalanceBreakdown() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: materialId } = useParams();
  const {
    contractor,
    contractorId,
    material,
    category,
    site,
    balanceQty,
    siteId,
    categoryId,
  } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [ledgerData, setLedgerData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  });

  useEffect(() => {
    fetchLedgerData();
  }, [materialId, pagination.current, pagination.pageSize]);

  const fetchLedgerData = async () => {
    try {
      setLoading(true);
      const response =
        await reportApis.fetchContractorMaterialFlowSummaryLedger({
          page: pagination.current,
          pageSize: pagination.pageSize,
          sortOrder: "ASC",
          siteIds: siteId,
          categoryIds: categoryId,
          materialIds: materialId,
          contractorIds: contractorId,
        });

      if ((response as any).status) {
        const data = (response as any).response.data || [];
        // Transform API data to match table structure
        const transformedData = data.map((item: any, index: number) => {
          const inward = parseFloat(item.quantityHandovered || 0);
          const outward =
            parseFloat(item.quantityUtilized || 0) +
            parseFloat(item.quantityDamaged || 0) +
            parseFloat(item.quantityReturned || 0);

          return {
            key: index,
            type: item.type,
            dateTime: dayjs(item.handoverDate).format("DD-MMM-YYYY"),
            siteName: item.siteName,
            material: item.Material,
            category: item.Category,
            uom: item.UOM,
            inwardQty: inward,
            outwardQty: outward,
            quantityHandovered: item.quantityHandovered,
            quantityUtilized: item.quantityUtilized,
            quantityDamaged: item.quantityDamaged,
            quantityReturned: item.quantityReturned,
            balance: parseFloat(item.balance || 0),
          };
        });

        setLedgerData(transformedData);
        setPagination({
          ...pagination,
          total: parseInt(
            (response as any).response.pagination?.totalItems || 0
          ),
        });
      }
    } catch (error) {
      console.error("Error fetching ledger data:", error);
    } finally {
      setLoading(false);
    }
  };

  const breakdownColumns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
    },
    {
      title: "Date",
      dataIndex: "dateTime",
      key: "dateTime",
      width: 150,
    },
    {
      title: "Site",
      dataIndex: "siteName",
      key: "siteName",
      width: 200,
    },
    {
      title: "Handovered Qty",
      dataIndex: "quantityHandovered",
      key: "quantityHandovered",
      width: 120,
      render: (text: any) => (
        <span style={{ color: "green", fontWeight: "bold" }}>
          {text ? parseFloat(text).toFixed(3) : "-"}
        </span>
      ),
    },
    {
      title: "Utilized Qty",
      dataIndex: "quantityUtilized",
      key: "quantityUtilized",
      width: 120,
      render: (text: any) => (
        <span style={{ color: "red", fontWeight: "bold" }}>
          {text ? parseFloat(text).toFixed(3) : "-"}
        </span>
      ),
    },
    {
      title: "Damaged Qty",
      dataIndex: "quantityDamaged",
      key: "quantityDamaged",
      width: 120,
      render: (text: any) => (
        <span style={{ color: "red", fontWeight: "bold" }}>
          {text ? parseFloat(text).toFixed(3) : "-"}
        </span>
      ),
    },
    {
      title: "Returned Qty",
      dataIndex: "quantityReturned",
      key: "quantityReturned",
      width: 120,
      render: (text: any) => (
        <span style={{ color: "orange", fontWeight: "bold" }}>
          {text ? parseFloat(text).toFixed(3) : "-"}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 120,
      render: (text: any) => (
        <span
          style={{
            color: text > 0 ? "green" : text < 0 ? "red" : "black",
            fontWeight: "bold",
          }}
        >
          {parseFloat(text).toFixed(3)}
        </span>
      ),
    },
  ];

  // Calculate totals from ledger data
  const totals = ledgerData.reduce(
    (acc, item: any) => ({
      handovered: acc.handovered + parseFloat(item.quantityHandovered || 0),
      utilized: acc.utilized + parseFloat(item.quantityUtilized || 0),
      damaged: acc.damaged + parseFloat(item.quantityDamaged || 0),
      returned: acc.returned + parseFloat(item.quantityReturned || 0),
    }),
    { handovered: 0, utilized: 0, damaged: 0, returned: 0 }
  );

  // Use the balance from the main report (passed via state) for consistency
  const finalBalance = balanceQty || 0;

  return (
    <>
      <Head title="Material Balance Breakdown" />
      <Content>
        <Card className="card-bordered">
          <BlockHead size="lg">
            <BlockBetween>
              <BlockHeadContent>
                <BlockTitle tag="h4">
                  <span>Material Balance Breakdown</span>
                </BlockTitle>
                <p className="text-muted mb-0">
                  Detailed breakdown of material flow calculations
                </p>
              </BlockHeadContent>
              <div className="nk-block-head-content">
                <ul className="nk-block-tools g-3">
                  <li className="pt-0 pb-0">
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      onClick={async () => {
                        navigate(
                          "/inventory-material-flow-by-contractor-reports-list"
                        );
                      }}
                    >
                      <Icon name="arrow-left" />
                      <span>Back</span>
                    </Button>
                  </li>
                </ul>
              </div>
            </BlockBetween>
          </BlockHead>
          <div className="card-inner">
            <Descriptions
              title="Material Information"
              bordered
              column={2}
              size="small"
              className="mb-4"
            >
              <Descriptions.Item label="Contractor">
                {contractor}
              </Descriptions.Item>
              <Descriptions.Item label="Site">{site}</Descriptions.Item>
              <Descriptions.Item label="Category">{category}</Descriptions.Item>
              <Descriptions.Item label="Material">{material}</Descriptions.Item>
              <Descriptions.Item label="Balance Quantity" span={2}>
                <span
                  style={{
                    color: balanceQty > 0 ? "green" : "red",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  {balanceQty}
                </span>
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            <h5 className="mb-3">Material Flow Ledger</h5>
            <Spin spinning={loading}>
              <Table
                columns={breakdownColumns}
                dataSource={ledgerData}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  onChange: (page, pageSize) => {
                    setPagination({ ...pagination, current: page, pageSize });
                  },
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} transactions`,
                }}
                size="small"
                bordered
                scroll={{ x: 1200 }}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={3}>
                        <strong>Total</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <strong style={{ color: "green" }}>
                          {totals.handovered.toFixed(3)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <strong style={{ color: "red" }}>
                          {totals.utilized.toFixed(3)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>
                        <strong style={{ color: "red" }}>
                          {totals.damaged.toFixed(3)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>
                        <strong style={{ color: "orange" }}>
                          {totals.returned.toFixed(3)}
                        </strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>
                        <span
                          style={{
                            color:
                              finalBalance > 0
                                ? "green"
                                : finalBalance < 0
                                ? "red"
                                : "black",
                            fontWeight: "bold",
                            fontSize: "14px",
                          }}
                        >
                          {finalBalance.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            </Spin>
          </div>
        </Card>
      </Content>
    </>
  );
}

export default MaterialBalanceBreakdown;
